// The ad and analytics connections an activity or the whole organization can make. The keys live in
// events.additional_settings and organizations.tracking; the activity's own value wins, and an
// empty id switches the organization's off for that activity (get_event, trackingSettings).
export type Integration = 'meta' | 'reddit' | 'tiktok' | 'ga4';
export type IntegrationValues = { id: string; token: string };
export type IntegrationDraft = IntegrationValues & { on: boolean };

export const INTEGRATIONS: Record<Integration, {
  /** The brand, as the rows and the summary name it. */
  name: string;
  idKey: string; tokenKey: string; icon: string | string[];
  /** i18n keys: what it is for, the two fields and why the token matters */
  sub: string; idLabel: string; tokenLabel: string; tokenHint: string;
  /** What a real value looks like, shown in the empty field */
  idExample: string; tokenExample: string;
  help: string;
  /** Server-side only: without the token the connection does nothing. */
  tokenRequired: boolean;
  /** i18n key of the message for an id that cannot be right, when it cannot */
  checkId?: (id: string) => string | null;
}> = {
  meta: {
    name: 'Meta', idKey: 'meta_pixel_id', tokenKey: 'meta_access_token', icon: ['fab', 'meta'],
    sub: 'integration_meta_sub', idLabel: 'meta_pixel_id', tokenLabel: 'meta_access_token', tokenHint: 'integration_token_optional',
    idExample: '123456789012345', tokenExample: 'EAAGm0PX4ZCpsBA…',
    help: 'https://help.tickable.nl/hc/docs/articles/1790021278-hoe-koppel-ik-een-meta_-of-reddit_pixel-aan-mijn-ticketshop',
    tokenRequired: false,
    // several pixels are separated by commas
    checkId: (id) => (/^[\d\s,]+$/.test(id) ? null : 'integration_error_meta'),
  },
  tiktok: {
    name: 'TikTok', idKey: 'tiktok_pixel_id', tokenKey: 'tiktok_access_token', icon: ['fab', 'tiktok'],
    sub: 'integration_tiktok_sub', idLabel: 'tiktok_pixel_id', tokenLabel: 'tiktok_access_token', tokenHint: 'integration_token_tiktok',
    idExample: 'CABC1DEF2GH3IJ4KL5M6', tokenExample: '4f9c2a7e01d3…b81d',
    help: 'https://help.tickable.nl/hc/docs/articles/1790021278-hoe-koppel-ik-een-meta_-of-reddit_pixel-aan-mijn-ticketshop',
    tokenRequired: true,
  },
  ga4: {
    name: 'Google Analytics', idKey: 'ga4_measurement_id', tokenKey: 'ga4_api_secret', icon: ['fab', 'google'],
    sub: 'integration_ga4_sub', idLabel: 'ga4_measurement_id', tokenLabel: 'ga4_api_secret', tokenHint: 'integration_token_ga4',
    idExample: 'G-AB12CD34EF', tokenExample: 'Xy7aBcDeFgH1234iJkL',
    help: 'https://help.tickable.nl/hc/docs/articles/1790021278-hoe-koppel-ik-een-meta_-of-reddit_pixel-aan-mijn-ticketshop',
    tokenRequired: false,
    // the usual mix-up: a Tag Manager (GTM-) or old Universal Analytics (UA-) id
    checkId: (id) => (/^G-[A-Z0-9]+$/i.test(id) ? null : 'integration_error_ga4'),
  },
  reddit: {
    name: 'Reddit', idKey: 'reddit_pixel_id', tokenKey: 'reddit_access_token', icon: ['fab', 'reddit'],
    sub: 'integration_reddit_sub', idLabel: 'reddit_pixel_id', tokenLabel: 'reddit_access_token', tokenHint: 'integration_token_optional',
    idExample: 'a2_abc123def456', tokenExample: 'eyJhbGciOiJSUzI1NiIs…',
    help: 'https://help.tickable.nl/hc/docs/articles/1790021278-hoe-koppel-ik-een-meta_-of-reddit_pixel-aan-mijn-ticketshop',
    tokenRequired: false,
  },
};

// most used first
export const INTEGRATION_KEYS: Integration[] = ['meta', 'tiktok', 'ga4', 'reddit'];

/** The connection's id and token in a settings object, '' when not set. */
export const integrationValues = (settings: unknown, integration: Integration): IntegrationValues => {
  const s = (settings ?? {}) as Record<string, unknown>;
  const { idKey, tokenKey } = INTEGRATIONS[integration];
  return { id: typeof s[idKey] === 'string' ? s[idKey] as string : '', token: typeof s[tokenKey] === 'string' ? s[tokenKey] as string : '' };
};

/**
 * The one row the settings pages show for all connections, so someone who never ran an ad sees one
 * line instead of four platforms; their page opens behind it (`open`). It names the ones that are on.
 */
export const integrationsSummary = (on: Integration[], t: (key: string, values?: Record<string, unknown>) => string,
  locale: string, open: () => void) => ({
  label: t('integrations_tile'),
  icon: 'chart-line',
  description: on.length
    ? t('integrations_tile_on', { names: new Intl.ListFormat(locale, { type: 'conjunction' }).format(on.map((key) => INTEGRATIONS[key].name)) })
    : t('integrations_tile_description'),
  visible: on.length > 0,
  noToggle: true,
  onEnable: open,
  onChange: () => {},
});
